import React, { useState, useEffect, useMemo } from 'react';
import { message } from 'antd';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './index.less';

interface Track {
  buttonPosition: string;
  cardNumber: string;
  chineseName: string;
  description: string;
  englishName: string;
  fileDirectory: string;
  frames: number;
  hashValue: string;
  id: number;
  level: string;
  songId: number;
  path: string;
  waitForNextLoop: boolean;
  waitingToRemove?: boolean;
  bpm: number;
}

interface Song {
  id: number;
  englishName: string;
  chineseName: string;
  loopDuration: number;
  bpm: number;
  file_directory: string;
  tracks: {
      beats: Track[];
      effects: Track[];
      melodys: Track[];
      vocals: Track[];
  };
}

interface ScanHashProps {
  hash: string;
  onCancel: () => void;
  onCollect: (info: { bpm: number; songId: number }) => void;
}

const ScanHash: React.FC<ScanHashProps> = ({ hash, onCancel, onCollect }) => {
  const [track, setTrack] = useState<Track | null>(null);
  const [songs, setSongs] = useState<Song[]>([]);
  const navigate = useNavigate();

  // 计算当前 track 所属歌曲的 bpm
  const trackBpm = useMemo(() => {
    if (!track) return undefined;
    const song = songs.find(s => s.id === track.songId);
    return song?.bpm;
  }, [track, songs]);

  useEffect(() => {
    if (hash) {
      const savedHashes = localStorage.getItem('scanedhash');
      if (savedHashes) {
        const hashList = JSON.parse(savedHashes);
        if (hashList.includes(hash)) {
          message.success('已收藏');
          if (track && trackBpm) {
            onCollect({ bpm: trackBpm, songId: track.songId });
          }
          return;
        }
      }
    }
  }, [hash, onCollect, track, trackBpm]);

  useEffect(() => {
    const fetchSongs = async () => {
      try {
        const response = await axios.get('/api/v1/incredibox/songs');
        setSongs(response.data);
      } catch (error) {
        console.error('获取歌曲列表失败:', error);
      }
    };
    fetchSongs();
  }, []);

  useEffect(() => {
    if (hash) {
      fetch(`/api/v1/incredibox/tracks-by-hash/${hash}`)
        .then(async response => {
          if (response.ok) {
            const data = await response.json();
            if (data && data.length > 0) {
              data.forEach((track: Track) => {
                const song = songs.find(s => s.id === track.songId);
                if (song) {
                  track.path = `${song.englishName}/${track.fileDirectory}`;
                }
              });
              setTrack(data[0]);
            } else {
              message.warning('未找到相关数据');
              onCancel();
            }
          }
        })
        .catch(error => {
          message.error('获取数据失败');
          onCancel();
        });
    }
  }, [hash, songs, onCancel]);

  const handleCollect = () => {
    if (hash) {
      const savedHashes = localStorage.getItem('scanedhash');
      let hashArray = savedHashes ? JSON.parse(savedHashes) : [];
      
      if (!hashArray.includes(hash)) {
        hashArray.push(hash);
        localStorage.setItem('scanedhash', JSON.stringify(hashArray));
        message.success('收藏成功');

        // 添加GA统计事件
        if (typeof gtag !== 'undefined') {
          gtag('event', 'collect_track', {
            event_category: 'scan_hash',
            event_label: hash,
            value: track?.songId || 0
          });
        }

      }
      
      if (track && trackBpm) {
        onCollect({ bpm: trackBpm, songId: track.songId });
        navigate(`/?bpm=${trackBpm}&songId=${track.songId}`);
      }
    }
  };

  if (!track) return null;

  return (
    <div className='scan-hash-page'>
      <div className="track-info">
        <div className="thumb" style={{ backgroundImage: `url(/assets/images/avatar_bg.png)` }}>
          <img src={`https://oss.usecubes.cn/incredibox/assets/files/${track.path}/${track.fileDirectory}_0.svg`} alt="" />
        </div>
        <div className="infos">
          <div className="avatar">
            <img src={`https://oss.usecubes.cn/incredibox/assets/files/${track.path}/icon.svg`} alt={track.chineseName} />
          </div>
          <div className="track-name">{track.chineseName}</div>
        </div>
        <div className="track-actions">
          <button className="cancel-button" onClick={onCancel}>取消</button>
          <button className="collect-button" onClick={handleCollect}>+ 收藏</button>
        </div>
      </div>
    </div>
  );
};

export default ScanHash; 